export interface MockMaintenanceTask {
    id: string;
    taskName: string;
    description: string;
    requiresSpares: boolean;
    spareParts: MockSparePart[];
}

export interface MockSparePart {
    itemId: string | null;
    itemName: string;
    qty: number;
    unit: string;
}

export interface MockMaintenanceSchedule {
    id: string;
    machineId: string;
    machineName: string;
    scheduleType: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ANNUAL' | 'CUSTOM';
    /** Interval in days for CUSTOM schedules (set from the Machine master) */
    frequencyDays?: number | null;
    taskName: string;
    description: string;
    estimatedDurationMinutes: number;
    assignedTo: string;
    tasks: MockMaintenanceTask[];
    lastDoneDate: string | null;
    nextDueDate: string;
    advanceAlertDays: number;
    isActive: boolean;
}

export interface MockSpareUsed {
    itemName: string;
    qty: number;
    unit: string;
}

export interface MockMaintenanceWorkOrder {
    id: string;
    mwoNumber: string;
    scheduleId: string;
    machineId: string;
    machineName: string;
    scheduleType: string;
    taskName: string;
    scheduledDate: string;
    status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE' | 'SKIPPED';
    assignedTo: string;
    actualStartTime: string | null;
    actualEndTime: string | null;
    durationMinutes: number | null;
    completedTasks: string[];
    sparesUsed: MockSpareUsed[];
    findings: string | null;
    nextActionRequired: string | null;
    completedBy: string | null;
    completedAt: string | null;
    skipReason?: string;
}

export let mockMaintenanceSchedules: MockMaintenanceSchedule[] = [
    {
        id: 'ms-001',
        machineId: 'mch-001',
        machineName: 'Press Machine PM-01',
        scheduleType: 'DAILY',
        taskName: 'Daily Pre-shift Inspection',
        description: 'Check oil levels, lubrication, safety guards',
        estimatedDurationMinutes: 30,
        assignedTo: 'Raju Operator',
        lastDoneDate: '2026-09-06',
        nextDueDate: '2026-09-07',
        advanceAlertDays: 0,
        isActive: true,
        tasks: [
            {
                id: 't-001',
                taskName: 'Check hydraulic oil level',
                description: 'Min 3/4 level in sight glass',
                requiresSpares: false,
                spareParts: [],
            },
            {
                id: 't-002',
                taskName: 'Lubricate guide pillars',
                description: 'Apply grease to all 4 guide pillars',
                requiresSpares: true,
                spareParts: [{ itemId: null, itemName: 'Machine Grease', qty: 0.1, unit: 'Kg' }],
            },
            {
                id: 't-003',
                taskName: 'Check safety guards',
                description: 'Ensure all guards are in place',
                requiresSpares: false,
                spareParts: [],
            },
        ],
    },
    {
        id: 'ms-002',
        machineId: 'mch-002',
        machineName: 'Injection Moulding IM-01',
        scheduleType: 'WEEKLY',
        taskName: 'Weekly Barrel Cleaning',
        description: 'Clean barrel, check temperatures, lubricate toggle',
        estimatedDurationMinutes: 120,
        assignedTo: 'Suresh Technician',
        lastDoneDate: '2026-08-31',
        nextDueDate: '2026-09-07',
        advanceAlertDays: 1,
        isActive: true,
        tasks: [
            {
                id: 't-004',
                taskName: 'Barrel purging',
                description: 'Purge with virgin material',
                requiresSpares: true,
                spareParts: [{ itemId: null, itemName: 'Purging Compound', qty: 2, unit: 'Kg' }],
            },
            {
                id: 't-005',
                taskName: 'Toggle lubrication',
                description: 'Grease all toggle pins',
                requiresSpares: true,
                spareParts: [{ itemId: null, itemName: 'Toggle Grease', qty: 0.05, unit: 'Kg' }],
            },
        ],
    },
    {
        id: 'ms-003',
        machineId: 'mch-001',
        machineName: 'Press Machine PM-01',
        scheduleType: 'MONTHLY',
        taskName: 'Monthly Full Service',
        description: 'Oil change, filter replacement, full inspection',
        estimatedDurationMinutes: 240,
        assignedTo: 'External Service Team',
        lastDoneDate: '2026-08-01',
        nextDueDate: '2026-09-01',
        advanceAlertDays: 7,
        isActive: true,
        tasks: [
            {
                id: 't-006',
                taskName: 'Hydraulic oil change',
                description: 'Drain and refill with HLP-46 oil',
                requiresSpares: true,
                spareParts: [{ itemId: null, itemName: 'HLP-46 Hydraulic Oil', qty: 20, unit: 'Ltr' }],
            },
            {
                id: 't-007',
                taskName: 'Replace oil filter',
                description: 'Replace hydraulic oil filter element',
                requiresSpares: true,
                spareParts: [{ itemId: null, itemName: 'Oil Filter Element', qty: 1, unit: 'Nos' }],
            },
        ],
    },
];

export let mockMaintenanceMWOs: MockMaintenanceWorkOrder[] = [
    {
        id: 'mwo-001',
        mwoNumber: 'MWO-2026-001',
        scheduleId: 'ms-003',
        machineId: 'mch-001',
        machineName: 'Press Machine PM-01',
        scheduleType: 'MONTHLY',
        taskName: 'Monthly Full Service',
        scheduledDate: '2026-09-01',
        status: 'OVERDUE',
        assignedTo: 'External Service Team',
        actualStartTime: null,
        actualEndTime: null,
        durationMinutes: null,
        completedTasks: [],
        sparesUsed: [],
        findings: null,
        nextActionRequired: null,
        completedBy: null,
        completedAt: null,
    },
    {
        id: 'mwo-002',
        mwoNumber: 'MWO-2026-002',
        scheduleId: 'ms-001',
        machineId: 'mch-001',
        machineName: 'Press Machine PM-01',
        scheduleType: 'DAILY',
        taskName: 'Daily Pre-shift Inspection',
        scheduledDate: '2026-09-07',
        status: 'PENDING',
        assignedTo: 'Raju Operator',
        actualStartTime: null,
        actualEndTime: null,
        durationMinutes: null,
        completedTasks: [],
        sparesUsed: [],
        findings: null,
        nextActionRequired: null,
        completedBy: null,
        completedAt: null,
    },
    {
        id: 'mwo-003',
        mwoNumber: 'MWO-2026-003',
        scheduleId: 'ms-002',
        machineId: 'mch-002',
        machineName: 'Injection Moulding IM-01',
        scheduleType: 'WEEKLY',
        taskName: 'Weekly Barrel Cleaning',
        scheduledDate: '2026-08-31',
        status: 'COMPLETED',
        assignedTo: 'Suresh Technician',
        actualStartTime: '2026-08-31T08:00:00Z',
        actualEndTime: '2026-08-31T10:05:00Z',
        durationMinutes: 125,
        completedTasks: ['t-004', 't-005'],
        sparesUsed: [
            { itemName: 'Purging Compound', qty: 1.8, unit: 'Kg' },
            { itemName: 'Toggle Grease', qty: 0.05, unit: 'Kg' },
        ],
        findings: 'Toggle pin 3 showing wear — monitor closely',
        nextActionRequired: 'Replace toggle pin 3 at next service',
        completedBy: 'Suresh Technician',
        completedAt: '2026-08-31T10:05:00Z',
    },
];

// ── AMC (Annual Maintenance Contract) ──

export interface MockAMCContract {
    id: string;
    contractNumber: string;
    vendorName: string;
    vendorPhone: string;
    vendorEmail: string;
    machineIds: string[];
    machineNames: string[];
    contractType: 'COMPREHENSIVE' | 'PARTS_ONLY' | 'LABOUR_ONLY';
    startDate: string;
    endDate: string;
    annualCost: number;
    visitFrequency: string;
    nextVisitDate: string | null;
    status: 'ACTIVE' | 'EXPIRED' | 'EXPIRING_SOON';
}

export let mockAMCContracts: MockAMCContract[] = [
    {
        id: 'amc-001',
        contractNumber: 'AMC-2026-001',
        vendorName: 'PressServ Solutions Pvt Ltd',
        vendorPhone: '9876543210',
        vendorEmail: 'service@pressserv.com',
        machineIds: ['mch-001'],
        machineNames: ['Press Machine PM-01'],
        contractType: 'COMPREHENSIVE',
        startDate: '2026-04-01',
        endDate: '2027-03-31',
        annualCost: 120000,
        visitFrequency: 'Quarterly',
        nextVisitDate: '2026-10-01',
        status: 'ACTIVE',
    },
    {
        id: 'amc-002',
        contractNumber: 'AMC-2026-002',
        vendorName: 'Mould Tech Services',
        vendorPhone: '9988776655',
        vendorEmail: 'service@mouldtech.com',
        machineIds: ['mch-002'],
        machineNames: ['Injection Moulding IM-01'],
        contractType: 'PARTS_ONLY',
        startDate: '2025-06-01',
        endDate: '2026-05-31',
        annualCost: 45000,
        visitFrequency: 'Monthly',
        nextVisitDate: null,
        status: 'EXPIRED',
    },
];

export function getNextDueDate(scheduleType: string, fromDate?: string): string {
    const base = fromDate ? new Date(fromDate) : new Date();
    switch (scheduleType) {
        case 'DAILY':
            base.setDate(base.getDate() + 1);
            break;
        case 'WEEKLY':
            base.setDate(base.getDate() + 7);
            break;
        case 'MONTHLY':
            base.setMonth(base.getMonth() + 1);
            break;
        case 'QUARTERLY':
            base.setMonth(base.getMonth() + 3);
            break;
        case 'ANNUAL':
            base.setFullYear(base.getFullYear() + 1);
            break;
    }
    return base.toISOString().slice(0, 10);
}

let mwoCounter = 3;
export function generateMWONumber(): string {
    mwoCounter += 1;
    return `MWO-2026-${String(mwoCounter).padStart(3, '0')}`;
}

export const machineOptions = [
    { id: 'mch-001', name: 'Press Machine PM-01' },
    { id: 'mch-002', name: 'Injection Moulding IM-01' },
    { id: 'mch-003', name: 'CNC Lathe CL-02' },
    { id: 'mch-004', name: 'Grinding Machine GM-01' },
];